(() => {
  const SELECTOR = '[data-tsuki-token-vending]';

  class TsukiTokenVending {
    constructor(root) {
      if (root.dataset.vendingReady === 'true') return;
      root.dataset.vendingReady = 'true';

      this.root = root;
      this.signedIn = root.dataset.signedIn === 'true';
      this.statusUrl = root.dataset.statusUrl;
      this.claimUrl = root.dataset.claimUrl;
      this.redeemUrl = root.dataset.redeemUrl;
      this.outOfOrder = root.dataset.outOfOrder === 'true';
      this.goal = Number(root.dataset.tokenGoal) || 7;
      this.claimButton = root.querySelector('[data-claim-button]');
      this.redeemButton = root.querySelector('[data-redeem-button]');
      this.statusText = root.querySelector('[data-status-text]');
      this.balanceText = root.querySelector('[data-balance-text]');
      this.nextClaimLabel = root.querySelector('[data-next-label]');
      this.nextClaimText = root.querySelector('[data-next-claim]');
      this.machineCountdownLabel = root.querySelector('[data-machine-countdown-label]');
      this.machineCountdown = root.querySelector('[data-machine-countdown]');
      this.message = root.querySelector('[data-live-message]');
      this.ledger = root.querySelector('[data-token-ledger]');
      this.tokenLayer = root.querySelector('[data-token-layer]');
      this.tokenTray = root.querySelector('[data-token-tray]');
      this.modal = root.querySelector('[data-login-modal]');
      this.modalClose = root.querySelector('[data-modal-close]');
      this.balance = 0;
      this.canClaim = this.signedIn;
      this.nextClaimAt = null;
      this.cycleComplete = false;
      this.cycleResetsAt = null;
      this.resetRefreshAttempted = false;
      this.busy = false;
      this.timer = null;

      if (this.outOfOrder) {
        this.root.classList.add('is-out-of-order');
        this.root.setAttribute('aria-busy', 'false');
        if (this.claimButton) this.claimButton.disabled = true;
        if (this.redeemButton) this.redeemButton.disabled = true;
        if (this.statusText) this.statusText.textContent = 'TEMPORARILY UNAVAILABLE';
        if (this.balanceText) this.balanceText.textContent = '-- / 7';
        if (this.nextClaimText) this.nextClaimText.textContent = 'COME BACK SOON';
        if (this.machineCountdown) this.machineCountdown.textContent = 'OFFLINE';
        if (this.message) this.message.textContent = 'OUT OF ORDER — COME BACK SOON';
        return;
      }

      this.claimButton?.addEventListener('click', () => this.claim());
      this.redeemButton?.addEventListener('click', () => this.redeem());
      this.modalClose?.addEventListener('click', () => this.closeModal());
      this.modal?.addEventListener('click', (event) => {
        if (event.target === this.modal) this.closeModal();
      });

      this.render({ balance: 0, canClaim: this.signedIn });
      if (this.signedIn) this.loadStatus();
    }

    async request(url, options = {}) {
      if (!url) throw new Error('REWARDS CONNECTION NOT CONFIGURED');

      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...options.headers
        },
        ...options
      });

      const contentType = response.headers.get('content-type') || '';
      const payload = contentType.includes('application/json') ? await response.json() : null;
      if (!response.ok || !payload) {
        throw new Error(payload?.message || 'THE TOKEN MACHINE IS TEMPORARILY OFFLINE');
      }
      return payload;
    }

    async loadStatus() {
      if (!this.statusUrl) return;
      this.setBusy(true, 'CHECKING TOKEN CARD');
      try {
        const payload = await this.request(this.statusUrl, { method: 'GET' });
        this.render(payload);
        this.setMessage(payload.message || 'TOKEN CARD READY');
      } catch (error) {
        this.setMessage(error.message, true);
        this.statusText.textContent = 'MACHINE OFFLINE';
      } finally {
        this.setBusy(false);
      }
    }

    async claim() {
      if (!this.signedIn) {
        this.openModal();
        return;
      }
      if (this.cycleComplete) {
        this.setMessage('THIS MONTH’S TOKEN RUN IS COMPLETE — COME BACK NEXT MONTH');
        return;
      }
      if (this.busy) return;

      this.setBusy(true, 'VENDING TOKEN');
      try {
        const payload = await this.request(this.claimUrl, {
          method: 'POST',
          body: JSON.stringify({ action: 'claim_daily_token' })
        });

        if (payload.claimed === false) {
          this.render(payload);
          this.setMessage(payload.message || 'TODAY’S TOKEN HAS ALREADY BEEN CLAIMED');
          return;
        }

        await this.dropToken(payload.tokenVariant);
        const nextClaimAt = payload.nextClaimAt || new Date(Date.now() + 86400000).toISOString();
        this.render({ ...payload, nextClaimAt, canClaim: false, newlyCollected: true });
        this.setMessage(payload.message || 'TOKEN COLLECTED — NEXT DROP UNLOCKS IN 24 HOURS');
      } catch (error) {
        this.setMessage(error.message, true);
        this.statusText.textContent = 'TRY AGAIN LATER';
      } finally {
        this.setBusy(false);
      }
    }

    async redeem() {
      if (!this.signedIn) {
        this.openModal();
        return;
      }
      if (this.busy || this.balance < this.goal || this.cycleComplete) return;

      this.setBusy(true, 'ISSUING REWARD');
      try {
        const payload = await this.request(this.redeemUrl, {
          method: 'POST',
          body: JSON.stringify({ action: 'redeem_store_credit' })
        });
        this.render({ ...payload, balance: this.goal, cycleComplete: payload.cycleComplete ?? true });
        this.setMessage(payload.message || 'STORE CREDIT ADDED — READY TO SPEND ON YOUR NEXT ORDER');
      } catch (error) {
        this.setMessage(error.message, true);
      } finally {
        this.setBusy(false);
      }
    }

    dropToken(variant) {
      return new Promise((resolve) => {
        const token = document.createElement('span');
        const colors = {
          teal: 'var(--vending-teal)',
          pink: 'var(--vending-pink)',
          gold: '#f0a51b',
          red: 'var(--vending-red)'
        };
        const names = Object.keys(colors);
        const selected = colors[variant] ? variant : names[Math.floor(Math.random() * names.length)];
        token.className = 'tsuki-vending-token-drop';
        token.style.setProperty('--token-color', colors[selected]);
        token.style.setProperty('--token-left', `${18 + Math.random() * 64}%`);
        const start = 28 + Math.floor(Math.random() * 3) * 99;
        token.style.setProperty('--token-start', `${start}px`);
        token.style.setProperty('--token-distance', `${Math.max(88, this.tokenLayer.clientHeight - start - 20)}px`);
        this.tokenLayer.appendChild(token);

        window.setTimeout(() => {
          this.tokenTray.classList.remove('tsuki-vending-machine__tray--hit');
          void this.tokenTray.offsetWidth;
          this.tokenTray.classList.add('tsuki-vending-machine__tray--hit');
        }, 980);

        window.setTimeout(() => {
          token.remove();
          resolve();
        }, 1450);
      });
    }

    render(payload = {}) {
      const previousBalance = this.balance;
      this.balance = Math.max(0, Math.min(this.goal, Number(payload.balance ?? this.balance) || 0));
      this.nextClaimAt = payload.nextClaimAt || null;
      if (Object.prototype.hasOwnProperty.call(payload, 'cycleComplete')) {
        this.cycleComplete = Boolean(payload.cycleComplete);
        if (!this.cycleComplete) {
          this.cycleResetsAt = null;
          this.resetRefreshAttempted = false;
        }
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'cycleResetsAt')) {
        this.cycleResetsAt = payload.cycleResetsAt || null;
      }
      const canClaim = payload.canClaim ?? (!this.nextClaimAt && this.signedIn && !this.cycleComplete);
      this.canClaim = Boolean(canClaim) && !this.cycleComplete;

      this.balanceText.textContent = `${this.balance} / ${this.goal}`;
      this.ledger.setAttribute('aria-label', `${this.balance} of ${this.goal} tokens collected`);
      this.ledger.querySelectorAll('[data-token-index]').forEach((token) => {
        const collected = Number(token.dataset.tokenIndex) <= this.balance;
        token.classList.toggle('is-collected', collected);
        token.classList.remove('is-new');
        if (payload.newlyCollected && Number(token.dataset.tokenIndex) === this.balance && this.balance > previousBalance) {
          void token.offsetWidth;
          token.classList.add('is-new');
        }
      });

      this.claimButton.disabled = this.busy || (this.signedIn && !this.canClaim) || this.cycleComplete;
      this.redeemButton.disabled = this.busy || this.balance < this.goal || this.cycleComplete;
      if (!this.signedIn) this.statusText.textContent = 'CUSTOMER LOGIN REQUIRED';
      else if (this.cycleComplete) this.statusText.textContent = 'MONTHLY RUN COMPLETE';
      else if (this.balance >= this.goal) this.statusText.textContent = 'REWARD READY';
      else this.statusText.textContent = canClaim ? 'TOKEN READY' : 'CLAIMED TODAY';
      this.updateCountdown();
    }

    updateCountdown() {
      window.clearInterval(this.timer);
      const targetAt = this.cycleComplete ? this.cycleResetsAt : this.nextClaimAt;
      this.nextClaimLabel.textContent = this.cycleComplete ? 'NEXT MONTHLY RUN' : 'NEXT TOKEN';
      this.machineCountdownLabel.textContent = this.cycleComplete ? 'MONTHLY RESET' : 'NEXT TOKEN IN';

      if (!targetAt) {
        const readyText = this.cycleComplete ? 'NEXT MONTH' : (this.signedIn ? 'READY NOW' : 'SIGN IN');
        this.nextClaimText.textContent = readyText;
        this.machineCountdown.textContent = readyText;
        return;
      }

      const tick = () => {
        const remaining = new Date(targetAt).getTime() - Date.now();
        if (remaining <= 0) {
          window.clearInterval(this.timer);
          if (this.cycleComplete) {
            this.nextClaimText.textContent = 'READY SOON';
            this.machineCountdown.textContent = 'READY SOON';
            if (!this.resetRefreshAttempted) {
              this.resetRefreshAttempted = true;
              window.setTimeout(() => this.loadStatus(), 1500);
            }
            return;
          }
          this.nextClaimAt = null;
          this.canClaim = true;
          this.nextClaimText.textContent = 'READY NOW';
          this.machineCountdown.textContent = 'READY NOW';
          this.claimButton.disabled = false;
          this.statusText.textContent = 'TOKEN READY';
          return;
        }
        const days = Math.floor(remaining / 86400000);
        const hours = Math.floor((remaining % 86400000) / 3600000);
        const minutes = Math.floor((remaining % 3600000) / 60000);
        const seconds = Math.floor((remaining % 60000) / 1000);
        const countdownText = this.cycleComplete
          ? `${days}D ${String(hours).padStart(2, '0')}H`
          : `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        this.nextClaimText.textContent = countdownText;
        this.machineCountdown.textContent = countdownText;
      };
      tick();
      if (new Date(targetAt).getTime() > Date.now()) {
        this.timer = window.setInterval(tick, 1000);
      }
    }

    setBusy(busy, label) {
      this.busy = busy;
      this.root.classList.toggle('is-vending', busy);
      this.root.setAttribute('aria-busy', String(busy));
      if (label) this.statusText.textContent = label;
      if (this.claimButton) this.claimButton.disabled = busy || (this.signedIn && !this.canClaim) || this.cycleComplete;
      if (this.redeemButton) this.redeemButton.disabled = busy || this.balance < this.goal || this.cycleComplete;
    }

    setMessage(text, isError = false) {
      this.message.textContent = text || '';
      this.message.classList.toggle('is-error', isError);
    }

    openModal() {
      if (typeof this.modal?.showModal === 'function') this.modal.showModal();
      else this.modal?.setAttribute('open', '');
    }

    closeModal() {
      if (typeof this.modal?.close === 'function') this.modal.close();
      else this.modal?.removeAttribute('open');
    }
  }

  const mount = (scope = document) => scope.querySelectorAll(SELECTOR).forEach((root) => new TsukiTokenVending(root));

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount());
  else mount();

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
})();
